import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { FaSearch, FaCalendarAlt, FaGlobe, FaMapMarkerAlt, FaExternalLinkAlt, FaFilter, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import CustomDateTimePicker from '../components/common/CustomDateTimePicker';

const Events = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  
  // Pagination states
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  
  // Get values from URL params
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const searchQuery = searchParams.get('search') || '';
  const isOnlineFilterParam = searchParams.get('is_online');
  const isOnlineFilter = isOnlineFilterParam === null ? null : isOnlineFilterParam === 'true';
  const dateFromParam = searchParams.get('date_from');
  const dateFrom = useMemo(() => {
    return dateFromParam ? new Date(dateFromParam) : null;
  }, [dateFromParam]);
  
  const dateToParam = searchParams.get('date_to');
  const dateTo = useMemo(() => {
    return dateToParam ? new Date(dateToParam) : null;
  }, [dateToParam]);
  
  // Local state for search input (for debouncing)
  const [searchInput, setSearchInput] = useState(searchQuery);
  
  // Debounce timer ref
  const debounceTimerRef = useRef(null);
  
  // Sync search input with URL param on mount or when URL changes
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);
  
  // Debounce search query and update URL
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      if (searchInput.trim()) {
        newParams.set('search', searchInput.trim());
      } else {
        newParams.delete('search');
      }
      newParams.set('page', '1'); // Reset to first page on search
      setSearchParams(newParams, { replace: true });
    }, 500); // 500ms debounce
    
    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput, searchParams, setSearchParams]);
  
  // Helper function to update URL params
  const updateURLParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams, { replace: true });
  };
  
  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        page_size: 10
      };
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (isOnlineFilter !== null) {
        params.is_online = isOnlineFilter;
      }
      if (dateFrom) {
        // Format date to YYYY-MM-DD
        const date = dateFrom instanceof Date ? dateFrom : new Date(dateFrom);
        params.date_from = date.toISOString().split('T')[0];
      }
      if (dateTo) {
        // Format date to YYYY-MM-DD
        const date = dateTo instanceof Date ? dateTo : new Date(dateTo);
        params.date_to = date.toISOString().split('T')[0];
      }
      
      const response = await api.get('/dashboard/events/', { params });
      
      // Handle paginated response
      if (response.data.results) {
        setEvents(response.data.results);
        setTotalCount(response.data.count || 0);
        setNextPage(response.data.next ? currentPage + 1 : null);
        setPreviousPage(response.data.previous ? currentPage - 1 : null);
      } else {
        // Fallback for non-paginated response
        const eventsArray = Array.isArray(response.data) ? response.data : [];
        setEvents(eventsArray);
        setTotalCount(eventsArray.length);
        setNextPage(null);
        setPreviousPage(null);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setEvents([]);
      setTotalCount(0);
      setNextPage(null);
      setPreviousPage(null);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, isOnlineFilter, dateFrom, dateTo, currentPage]);
  
  // Fetch events when URL params change
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('en-US', options);
  };
  
  const handleClearFilters = () => {
    setSearchParams({}, { replace: true });
    setSearchInput('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handlePageChange = (newPage) => {
    updateURLParams({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleSearchChange = (value) => {
    setSearchInput(value);
  };
  
  const handleOnlineFilterChange = (value) => {
    updateURLParams({ 
      is_online: value === null ? null : value.toString(),
      page: '1' // Reset to first page
    });
  };
  
  const handleDateFromChange = (date) => {
    if (date) {
      const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;
      updateURLParams({ 
        date_from: dateStr,
        page: '1' // Reset to first page
      });
    } else {
      updateURLParams({ 
        date_from: null,
        page: '1' // Reset to first page
      });
    }
  };
  
  const handleDateToChange = (date) => {
    if (date) {
      const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;
      updateURLParams({ 
        date_to: dateStr,
        page: '1' // Reset to first page
      });
    } else {
      updateURLParams({ 
        date_to: null,
        page: '1' // Reset to first page
      });
    }
  };
  
  // Filters component (reusable for sidebar and modal)
  const FiltersContent = () => (
    <>
      {/* Online/Offline Filter */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Event Type
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              name="eventType"
              checked={isOnlineFilter === null}
              onChange={() => handleOnlineFilterChange(null)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">All Events</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="eventType"
              checked={isOnlineFilter === true}
              onChange={() => handleOnlineFilterChange(true)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 flex items-center gap-1">
              <FaGlobe className="text-blue-500" />
              Online
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="eventType"
              checked={isOnlineFilter === false}
              onChange={() => handleOnlineFilterChange(false)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700 flex items-center gap-1">
              <FaMapMarkerAlt className="text-green-500" />
              Offline
            </span>
          </label>
        </div>
      </div>
      
      {/* Date Filters */}
      <div className="mb-4">
        <CustomDateTimePicker
          label="Date From"
          value={dateFrom}
          onChange={handleDateFromChange}
          placeholder="Select start date"
          showTimeSelect={false}
        />
      </div>
      
      <div className="mb-4">
        <CustomDateTimePicker
          label="Date To"
          value={dateTo}
          onChange={handleDateToChange}
          placeholder="Select end date"
          showTimeSelect={false}
          minDate={dateFrom}
        />
      </div>
      
      {/* Clear Filters */}
      {(searchQuery || isOnlineFilter !== null || dateFrom || dateTo) && (
        <button
          onClick={handleClearFilters}
          className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Clear Filters
        </button>
      )}
    </>
  );
  
  return (
    <div className="w-full px-6 md:px-12 py-6">
      <h1 className="text-2xl font-semibold mb-6 text-gray-900">Events</h1>
      
      {/* Mobile Search Bar - Always visible */}
      <div className="mb-4 lg:hidden">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search events..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      
      {/* Mobile Filter Button */}
      <div className="mb-4 lg:hidden flex items-center gap-2">
        <button
          onClick={() => setShowFiltersModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <FaFilter className="text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
          {(isOnlineFilter !== null || dateFrom || dateTo) && (
            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
              {[isOnlineFilter !== null, dateFrom, dateTo].filter(Boolean).length}
            </span>
          )}
        </button>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Desktop Left Sidebar - Filters */}
        <div className="hidden lg:block w-full lg:w-80 flex-shrink-0">
          <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-4">
            <h2 className="text-lg font-semibold mb-4 text-gray-900">Filters</h2>
            
            {/* Desktop Search */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <FiltersContent />
          </div>
        </div>
        
        {/* Mobile Filters Modal */}
        {showFiltersModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 lg:hidden">
            <div className="bg-white rounded-lg w-full max-w-md mt-12 max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <button
                  onClick={() => setShowFiltersModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4">
                <FiltersContent />
              </div>
            </div>
          </div>
        )}
        
        {/* Main Content - Events Feed */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading events...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchEvents}
                className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCalendarAlt className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No events found</h3>
              <p className="text-gray-600 mb-4">
                {searchQuery || isOnlineFilter !== null || dateFrom || dateTo
                  ? 'Try adjusting your filters to see more events.'
                  : 'There are no events available at the moment.'}
              </p>
              {(searchQuery || isOnlineFilter !== null || dateFrom || dateTo) && (
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {/* Event Image */}
                    {event.image_url && (
                      <div className="w-full h-64 bg-gray-100 overflow-hidden">
                        <img
                          src={event.image_url}
                          alt={event.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Event Content */}
                    <div className="p-6">
                      {/* Event Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {event.name}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <FaCalendarAlt className="text-gray-400" />
                              <span>{formatDateTime(event.date_time)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {event.is_online ? (
                                <>
                                  <FaGlobe className="text-blue-500" />
                                  <span className="text-blue-600">Online</span>
                                </>
                              ) : (
                                <>
                                  <FaMapMarkerAlt className="text-green-500" />
                                  <span className="text-green-600">Offline</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Event Description */}
                      {event.description && (
                        <p className="text-gray-700 mb-4 line-clamp-3">
                          {event.description}
                        </p>
                      )}
                      
                      {/* Event Link */}
                      {event.link && (
                        <a
                          href={event.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          <span>View Event</span>
                          <FaExternalLinkAlt className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {(nextPage || previousPage || currentPage > 1) && (
                <div className="mt-6 flex items-center justify-between bg-white rounded-lg border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">
                    Showing {events.length} of {totalCount} events
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(previousPage)}
                      disabled={!previousPage}
                      className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                        previousPage
                          ? 'border-gray-300 hover:bg-gray-50 text-gray-700'
                          : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <FaChevronLeft className="w-4 h-4" />
                      <span>Previous</span>
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-700">
                      Page {currentPage}
                    </span>
                    <button
                      onClick={() => handlePageChange(nextPage)}
                      disabled={!nextPage}
                      className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                        nextPage
                          ? 'border-gray-300 hover:bg-gray-50 text-gray-700'
                          : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <span>Next</span>
                      <FaChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Events;
